import { useCallback, useMemo, useState, isValidElement, cloneElement } from "react";
import { IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { faExpand, faCompress, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { LiveIndicator } from "../LiveIndicator/LiveIndicator";
import { RootState } from "../../state/store";
import { hasArea } from "../../utilities/helpers/roleUtilities";

type DashboardCardProps = {
  title?: string;
  linkTo?: string;
  linkTooltip?: string;
  linkArea?: string; // Note: matches the destination page's own `area` key in appRoutes.tsx
  children: React.ReactNode;
  colSpan?: {
    base?: number;
    md?: number;
    lg?: number;
    xl?: number;
    '2xl'?: number;
  };
  // Note: full-screen ignores this and fills the viewport, so it only sets the height on the grid.
  heightClass?: string;
  minHeightClass?: string;
};

export type LiveIndicatorState = {
  active: boolean;
  showLabel: boolean;
  tooltipText?: string;
};

const buildSpanClass = (colSpan?: DashboardCardProps["colSpan"]) => {
  if (!colSpan) return "";

  const map = {
    base: "",
    md: "md:",
    lg: "lg:",
    xl: "xl:",
    "2xl": "2xl:",
  };

  return Object.entries(colSpan)
    .map(([key, val]) => `${map[key as keyof typeof map]}col-span-${val}`)
    .join(" ");
};

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  linkTo,
  linkTooltip,
  linkArea,
  children,
  colSpan,
  heightClass = "h-[700px]",
  minHeightClass = "min-h-[450px]"
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [liveState, setLiveState] = useState<LiveIndicatorState>({
    active: false,
    showLabel: false,
  });
  const { user } = useSelector((s: RootState) => s.authReducer);

  const linkEnabled = useMemo(() => {
    if (!linkTo) return false;
    if (!linkArea) return true;
    return hasArea(user, linkArea);
  }, [linkTo, linkArea, user]);

  const handleLiveIndicatorChange = useCallback((state: LiveIndicatorState) => {
    setLiveState(state);
  }, []);

  const spanClass = buildSpanClass(colSpan);

  return (
    <>
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 animate-fadeIn"
          onClick={() => setIsFullscreen(false)}
        />
      )}

      <div
        className={clsx(
          "w-full",
          spanClass,
          isFullscreen && heightClass
        )}
      >
        <div
          className={clsx(
            "w-full flex flex-col border border-theme-border shadow-md shadow-theme-shadow bg-theme-surface-2 md:rounded-lg transition-shadow ease-in-out",
            minHeightClass,
            isFullscreen
              ? "fixed top-1/2 md:top-1/2 left-1/2 w-full h-screen md:w-[95vw] md:h-[90vh] z-50 -translate-x-1/2 -translate-y-1/2 overflow-auto"
              : clsx("relative w-full overflow-hidden hover:shadow-xl hover:shadow-theme-shadow", heightClass)
          )}
        >
          <div
            className={`${isFullscreen ? "pt-10 pb-6" : "py-4"} px-5 relative flex justify-center items-center border-b border-theme-border bg-theme-surface`}
          >
            {title && (
              <div className="flex items-center">
                {linkTo && linkEnabled ? (
                  <Link
                    to={linkTo}
                    title={linkTooltip ?? `Open ${title}`}
                    className={`group inline-flex items-start font-semibold text-theme-text hover:text-theme-text-muted transition-colors ${isFullscreen ? "text-3xl md:text-4xl" : "text-2xl"}`}
                  >
                    <span>{title}</span>
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      className={`transition-opacity mt-1 ${isFullscreen ? "text-sm md:text-md" : "text-[8px]"}`}
                    />
                  </Link>
                ) : (
                  <h3 className={`${isFullscreen ? "text-3xl md:text-4xl" : "text-2xl"} font-semibold text-theme-text`}>
                    {title}
                  </h3>
                )}
                <LiveIndicator
                  active={liveState.active}
                  label={liveState.showLabel ? "LIVE" : undefined}
                  tooltipText={liveState.tooltipText}
                  size={isFullscreen ? 15 : 12}
                  className="ml-2 mb-2 d-inline-block"
                />
              </div>
            )}

            <div className={`${isFullscreen ? "top-8" : "top-1/2"} absolute right-3 transform -translate-y-1/2`}>
              <IconButton
                onClick={() => setIsFullscreen((prev) => !prev)}
                className={`!bg-theme-surface hover:!bg-theme-surface-2 !text-theme-text transition-colors ${isFullscreen ? "!text-3xl" : "!text-lg"}`}
              >
                <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
              </IconButton>
            </div>
          </div>

          <div className="flex-grow flex justify-center overflow-y-hidden">
            {isValidElement(children)
              ? cloneElement(
                children as React.ReactElement<{
                  isFullScreen?: boolean;
                  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
                }>,
                {
                  isFullScreen: isFullscreen,
                  onLiveIndicatorChange: handleLiveIndicatorChange,
                }
              )
              : children}
          </div>
        </div>
      </div>
    </>
  );
};